import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { UserDAO } from '../../core/services/dao/userDAO';
import { Subscription } from 'rxjs';
import { LoginPersistenceService } from '../../core/services/loginPersistence.service';
import { User } from 'anchor-shared/lib/model/entity/user';
import { ContainsSubscriptions } from '../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../core/pure-functions/rx-helper';
import { AppMessagingService } from '../../core/services/app-messaging.service';
import { ErrorDialogService } from '../shared/errorDialog/errorDialogService';
import { CustomerAccountDialogService } from '../shared/dialogs/customer-account-dialog/customer-account-dialog-service';
import { AffiliateStatusService } from '../../core/services/affiliate-status.service';
import { DialogsService } from '../../core/services/dialog.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  model: any = {};
  googleUrl: string;
  subs: Subscription[] = [];
  params: string;
  redirectURL: string | UrlTree;
  isMobile = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthService,
    private userDAO: UserDAO,
    public loginPersistenceService: LoginPersistenceService,
    private appMessagingService: AppMessagingService,
    private ngZone: NgZone,
    private customerAccountDialogService: CustomerAccountDialogService,
    private errorDialogService: ErrorDialogService,
    private affiliateStatusService: AffiliateStatusService,
    private dialogService: DialogsService,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    const sub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.getGoogleAuthUrl();
    this.initOauthCheck();
    let params = this.route.snapshot.queryParams;
    if (params['redirectURL']) {
      if (params['SearchParams']) {
        this.redirectURL = params['redirectURL'] + '?' + params['SearchParams'];
      } else {
        this.redirectURL = params['redirectURL'];
      }
    }
    if (this.redirectURL === '/login') {
      this.redirectURL = null;
    }
  }

  login() {
    const sub = this.authenticationService
      .login(this.model.username, this.model.password)
      .subscribe((userIsAuthenticated: boolean) => {
        if (userIsAuthenticated) {
          let user = this.userDAO.getCurrentUser();
          this.clearAffiliateStatus();
          if (this.redirectURL) {
            this.router.navigateByUrl(this.redirectURL);
          } else if (user.acceptedAnchorCode || user.signupComplete) {
            if (this.loginPersistenceService.getHasBookingData()) {
              this.router.navigate([
                'captain-info/' + this.loginPersistenceService.getCaptainId(),
              ]);
            } else if (user.boatOwner) {
              this.router.navigate(['manage/boatyard']);
            } else if (isCustomer(user)) {
              this.router.navigate(['customer/charters']);
            } else {
              this.router.navigate(['manage']);
            }
          } else {
            if (userIsBroker(user)) {
              this.router.navigate(['manage']);
            } else if (isCustomer(user)) {
              this.router.navigate(['customer/charters']);
            } else if (userIsAffiliate(user)) {
              this.router.navigate(['manage/affiliate-dashboard']);
            } else {
              this.router.navigate(['sign-up/anchor-code']);
            }
          }
        }
      });
    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private getGoogleAuthUrl(): void {
    const googleAuthUrlSub = this.authenticationService
      .getGoogleAuthUrl({}, 'login')
      .subscribe((result) => {
        this.googleUrl = result.authUrl;
      });
    this.subs.push(googleAuthUrlSub);
  }

  /**
   * not working right now cause of the multiple oauth url issue
   */
  private initOauthCheck(): void {
    const code = this.route.snapshot.queryParams.code;
    if (code) {
      const googleTokenSub = this.authenticationService
        .getGoogleToken(code, 'login')
        .subscribe((tokens) => {
          const googleUserInfoSub = this.authenticationService
            .getGoogleUserInfo(tokens, 'login')
            .subscribe((userInfo) => {
              const findGoogleUserSub = this.userDAO
                .findUserByGoogleId(userInfo.id)
                .subscribe((foundUser) => {
                  if (foundUser && (foundUser.user || foundUser.id)) {
                    const googleAuthSub = this.authenticationService
                      .googleLogin(tokens, foundUser.email, 'login')
                      .subscribe((res) => {
                        this.router.navigate(['customer/charters']);
                      });
                    this.subs.push(googleAuthSub);
                  } else {
                    const createGoogleSub = this.authenticationService
                      .createGoogleUser(tokens)
                      .subscribe(
                        (res) => {
                          const googleAuthSub = this.authenticationService
                            .googleLogin(tokens, foundUser.email, 'login')
                            .subscribe((loginResponse) => {
                              this.customerOAuthDialog(
                                tokens,
                                loginResponse.user.email,
                                loginResponse.user.id,
                              );
                            });
                          this.subs.push(googleAuthSub);
                        },
                        (createError) => {
                          const sub = this.errorDialogService
                            .open(
                              createError.error.title,
                              createError.error.message,
                            )
                            .subscribe((completed) => {});
                          this.subs.push(sub);
                        },
                      );
                    this.subs.push(createGoogleSub);
                  }
                });
              this.subs.push(findGoogleUserSub);
            });
          this.subs.push(googleUserInfoSub);
        });
      this.subs.push(googleTokenSub);
    }
  }

  private customerOAuthDialog(
    tokens: any,
    userEmail: string,
    userId: number,
  ): void {
    const googleAuthSub = this.authenticationService
      .googleLogin(tokens, userEmail)
      .subscribe((res) => {
        this.customerAccountDialogService
          .open(userId, null, true)
          .subscribe((authSuccessful) => {
            if (authSuccessful) {
              this.router.navigate(['customer/charters']);
            } else {
            }
          });
      });
    this.subs.push(googleAuthSub);
  }

  private clearAffiliateStatus(): void {
    if (!this.userDAO.currentUserIs('Customer')) {
      this.affiliateStatusService.clearFromStorage();
    }
  }

  openLoginDialog(oAuthProvider: string) {
    const loginSub = this.dialogService
      .OauthResetDialog(oAuthProvider, this.isMobile)
      .subscribe((isLoggedIn) => {});
    this.subs.push(loginSub);
  }
}

export function userIsBroker(user: User) {
  let isBroker = false;
  user.roles.forEach((role) => {
    if (role.type === 'Broker') {
      isBroker = true;
    }
  });
  return isBroker;
}

export function userIsAffiliate(user: User) {
  let isAffiliate = false;
  user.roles.forEach((role) => {
    if (role.type === 'Affiliate') {
      isAffiliate = true;
    }
  });
  return isAffiliate;
}

export function isCustomer(user: User): boolean {
  let hasCustomerRole = false;
  if (user && user.roles) {
    user.roles.forEach((role) => {
      if (role.type === 'Customer') {
        hasCustomerRole = true;
      }
    });
  }
  return hasCustomerRole;
}

export function isCrew(user: User): boolean {
  let hasCrewRole = false;
  if (user && user.roles) {
    user.roles.forEach((role) => {
      if (role.type === 'Crew') {
        hasCrewRole = true;
      }
    });
  }
  return hasCrewRole;
}

export function userIsSpecifiedRole(user: User, roleType: String): boolean {
  let hasSpecificRole = false;
  if (user && user.roles) {
    user.roles.forEach((role) => {
      if (role.type === roleType) {
        hasSpecificRole = true;
      }
    });
  }
  return hasSpecificRole;
}
